import React, { ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SiteLayout } from '@shared/ui/SiteLayout';
import type { CtaLink, NavItem } from '@shared/ui/chrome';
import { useBrand } from '@shared/ui/brand';
import { Versioned, useUiVersion } from '@shared/ui/uiVersion';
import { useAuth } from '../contexts/AuthContext';
import { useAnalysisType } from '../contexts/AnalysisTypeContext';
import { useSiteName } from '../contexts/SiteConfigContext';
import { AnalysisTypeToggleCompact } from '../components/common/AnalysisTypeToggle';
import ClassicMainLayout from './ClassicMainLayout';

const SIGNED_OUT_ACTIONS: CtaLink[] = [
  { label: 'Join Now', to: '/register', primary: true },
  { label: 'Login', to: '/login' },
  { label: 'Preview', to: '/preview' },
  { label: 'Pricing', to: '/pricing' },
];

const FOOTER_LINKS: CtaLink[] = [
  { label: 'Pricing', to: '/pricing' },
  { label: 'Terms', to: '/terms' },
  { label: 'Privacy', to: '/privacy' },
  { label: 'Contact', to: '/contact' },
];

/** This app's frame: classic, or the new look for members who picked it (see useUiVersion). */
export const MainLayout: React.FC<{ children: ReactNode }> = ({ children }) => (
  <Versioned classic={<ClassicMainLayout>{children}</ClassicMainLayout>}>
    <NewLayout>{children}</NewLayout>
  </Versioned>
);

/** The new look's frame: the shared SiteLayout with this app's links and controls (ADR-0008). */
const NewLayout: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const brand = useBrand();
  const { isAuthenticated, isLoading, logout } = useAuth();
  const siteName = useSiteName();
  const { canChoose, switchTo } = useUiVersion();
  const { hasTrackStats, hasMultis, hasMultiBuilder } = useAnalysisType();

  // An unconfirmed sign-up (phone not yet verified) is sent back to verification.
  useEffect(() => {
    const tempPhoneNumber = localStorage.getItem('tempPhoneNumber');
    const tempUserId = localStorage.getItem('tempUserId');
    if (!isAuthenticated && !isLoading && tempPhoneNumber && tempUserId && !location.pathname.includes('/verify-phone')) {
      navigate('/verify-phone');
    }
  }, [isAuthenticated, isLoading, navigate, location.pathname]);

  const links: NavItem[] = [
    { label: brand.locksName, to: '/high-probability', kind: 'feature' },
    // Multis opens on Our multis, or on Build your own for a member with only the Multi Builder
    ...(hasMultis || hasMultiBuilder ? [{ label: 'Multis', to: hasMultis ? '/multis' : '/multis/build' }] : []),
    ...(hasTrackStats ? [{ label: 'Track stats', to: '/tracks' }] : []),
    { label: 'Account', to: '/profile' },
    { label: 'Pricing', to: '/pricing', mobileOnly: true },
    ...(canChoose ? [{ label: 'Classic look', onClick: () => switchTo('classic') }] : []),
    { label: 'Log out', onClick: () => void logout(), kind: 'danger' },
  ];

  return (
    <SiteLayout
      siteName={siteName}
      links={links}
      dateExtra={<AnalysisTypeToggleCompact />}
      queryDatePaths={['/high-probability', '/multis', '/multis/quaddies', '/multis/build', '/multis/mine']}
  signedOutMessage="Create an account to access daily racing tips"
      signedOutActions={SIGNED_OUT_ACTIONS}
      footerLinks={FOOTER_LINKS}
    >
      {children}
    </SiteLayout>
  );
};

export default MainLayout;
